import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getBankInstallments, getBinCode, getCreditLimit } from '../../api/endpoints/payment';
import type { InstallmentOption } from '../../api/types/payment';
import type { PaymentForm as PaymentFormValues, PaymentType } from '../../api/types/air';
import type { CurrentAccount } from '../../api/types/reports';

/** expirationYear/Month are exactly 2 chars ("30" = 2030); returnUrl is required —
 *  both confirmed via curl against the real MakePayment endpoint. Card fields are only
 *  required when `useCurrentPayment` is false (cari/running-account payment needs none). */
const schema = z
  .object({
    cardHolder: z.string().optional(),
    cardNumber: z.string().optional(),
    cvv: z.string().optional(),
    expirationMonth: z.string().optional(),
    expirationYear: z.string().optional(),
    installmentSelection: z.string().optional(),
    useCurrentPayment: z.boolean(),
    threeDSecure: z.boolean(),
    acceptTos: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (!values.acceptTos) {
      ctx.addIssue({
        code: 'custom',
        path: ['acceptTos'],
        message:
          'Kişisel Verilerin Korunması ve Gizlilik Politikası ile Acente ve Bilet Kurallarını kabul etmelisiniz',
      });
    }
    if (values.useCurrentPayment) return;
    if (!values.cardHolder) {
      ctx.addIssue({ code: 'custom', path: ['cardHolder'], message: 'Kart üzerindeki isim gerekli' });
    }
    if (
      !values.cardNumber ||
      values.cardNumber.length < 15 ||
      values.cardNumber.length > 19 ||
      !/^\d+$/.test(values.cardNumber)
    ) {
      ctx.addIssue({ code: 'custom', path: ['cardNumber'], message: 'Geçerli bir kart numarası girin' });
    }
    if (!values.cvv || values.cvv.length < 3 || values.cvv.length > 4) {
      ctx.addIssue({ code: 'custom', path: ['cvv'], message: 'Geçerli bir CVV girin' });
    }
    if (!values.expirationMonth || !/^(0[1-9]|1[0-2])$/.test(values.expirationMonth)) {
      ctx.addIssue({ code: 'custom', path: ['expirationMonth'], message: 'Geçerli bir ay girin (ör. 09)' });
    }
    if (!values.expirationYear || !/^\d{2}$/.test(values.expirationYear)) {
      ctx.addIssue({ code: 'custom', path: ['expirationYear'], message: 'Geçerli bir yıl girin (ör. 30)' });
    }
  });

type FormValues = z.infer<typeof schema>;

function formatBalance(limit?: number): string {
  if (limit === undefined || limit === null) return '—';
  return `${limit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
}

function installmentLabel(option: InstallmentOption): string {
  const n = option.installmentCount ?? 1;
  const count = n <= 1 ? 'Tek Çekim' : `${n} Taksit`;
  // Backend field is spelled "montlyPayment" (typo preserved — see api/types/pos.ts).
  const monthly =
    n > 1 && option.montlyPayment
      ? ` — ${option.montlyPayment.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺/ay`
      : '';
  const bank = option.bankName ? ` (${option.bankName})` : '';
  return `${count}${monthly}${bank}`;
}

export interface PaymentFormSubmit {
  paymentForm: PaymentFormValues | undefined;
  paymentType: PaymentType;
}

/**
 * Shared credit-card / cari payment form for every booking flow's payment step.
 * Generalizes the original air-only `AirPaymentForm`: adds a BIN lookup (bank name shown
 * once 6 digits are typed) and a bank-installment `<Select>` fed by
 * `Payment/GetBankInstallments`. Both are best-effort — if the endpoints 404 the form
 * silently falls back to a free-text installment count. Emits the same
 * `(paymentForm, paymentType)` contract the flows already consume.
 */
export function PaymentForm({
  amount,
  currency,
  loading,
  currentAccount,
  shoppingCartId,
  creditCardPaymentAllowed = true,
  threeDPaymentAllowed = false,
  submitLabel = 'Ödemeyi Tamamla',
  onSubmit,
}: {
  amount?: number;
  currency?: string;
  loading: boolean;
  currentAccount?: CurrentAccount;
  /** Enables the installment lookup against this cart. Omit to hide installments. */
  shoppingCartId?: string;
  creditCardPaymentAllowed?: boolean;
  threeDPaymentAllowed?: boolean;
  submitLabel?: string;
  onSubmit: (result: PaymentFormSubmit) => void;
}) {
  const currentPaymentInsufficient = !!currentAccount && (currentAccount.limit ?? 0) < (amount ?? 0);
  const currentPaymentAvailable = !!currentAccount && !currentPaymentInsufficient;

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      cardHolder: '',
      cardNumber: '',
      cvv: '',
      expirationMonth: '',
      expirationYear: '',
      installmentSelection: '1',
      useCurrentPayment: false,
      threeDSecure: threeDPaymentAllowed,
      acceptTos: false,
    },
  });

  const useCurrentPayment = watch('useCurrentPayment');
  const cardNumber = watch('cardNumber') ?? '';

  // Agency running-account ("cari") remaining limit — only fetched once the user actually
  // reaches for cari payment. Distinct from the per-account `currentAccount.limit` the flow
  // may pass in; shown as an extra reference line. Best-effort (route may be unavailable).
  const { data: creditLimitRes } = useQuery({
    queryKey: ['creditLimit'],
    queryFn: getCreditLimit,
    enabled: useCurrentPayment,
    staleTime: 60 * 1000,
  });

  const [binBank, setBinBank] = useState<string | null>(null);
  const [installments, setInstallments] = useState<InstallmentOption[]>([]);

  // BIN lookup + installment options once 6 digits are in. Debounced; failures are silent.
  useEffect(() => {
    if (useCurrentPayment) return;
    const digits = cardNumber.replace(/\D/g, '');
    if (digits.length < 6) {
      setBinBank(null);
      setInstallments([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const bin = await getBinCode(digits.slice(0, 8));
        if (!cancelled) setBinBank(bin.bin?.bank ?? null);
      } catch {
        if (!cancelled) setBinBank(null);
      }
      if (!shoppingCartId) return;
      try {
        const res = await getBankInstallments({ shoppingCartId, cardNumber: digits });
        if (!cancelled) setInstallments(res.installmentOptions ?? []);
      } catch {
        if (!cancelled) setInstallments([]);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cardNumber, shoppingCartId, useCurrentPayment]);

  const submit = handleSubmit((values) => {
    if (values.useCurrentPayment) {
      onSubmit({ paymentForm: undefined, paymentType: 'CurrentPayment' });
      return;
    }
    const chosen = installments.find(
      (o) => (o.installmentOptionId ?? String(o.installmentCount ?? 1)) === values.installmentSelection
    );
    onSubmit({
      paymentForm: {
        cardHolder: values.cardHolder,
        cardNumber: values.cardNumber,
        cvv: values.cvv,
        expirationMonth: values.expirationMonth,
        expirationYear: values.expirationYear,
        installmentId: chosen?.installmentOptionId,
        installmentCount: chosen
          ? chosen.installmentCount ?? 1
          : values.installmentSelection
            ? Number(values.installmentSelection)
            : 1,
        currency: (currency as PaymentFormValues['currency']) ?? 'TRY',
        returnUrl: `${window.location.origin}/payment/return?cart=${shoppingCartId ?? ''}`,
      },
      paymentType: values.threeDSecure ? 'ThreeDPayment' : 'CreditCardPayment',
    });
  });

  return (
    <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info">
        Ödenecek tutar: {amount != null ? `${amount.toLocaleString('tr-TR')} ${currency ?? 'TRY'}` : '—'}.
        Başarılı ödeme sonrası işlem tamamlanır.
      </Alert>

      {currentAccount && (
        <Box>
          <FormControlLabel
            control={
              <Controller
                control={control}
                name="useCurrentPayment"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={!currentPaymentAvailable}
                  />
                )}
              />
            }
            label={`Cari ödeme (Bakiye: ${formatBalance(currentAccount.limit)})`}
          />
          {currentPaymentInsufficient && (
            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
              Cari hesap bakiyesi ödenecek tutarı karşılamıyor — kredi limiti için lütfen ilgili birimle irtibata
              geçin.
            </Typography>
          )}
          {useCurrentPayment && creditLimitRes?.creditLimit != null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Kalan cari limit: {formatBalance(creditLimitRes.creditLimit)}
            </Typography>
          )}
        </Box>
      )}

      {!useCurrentPayment && creditCardPaymentAllowed && (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Kart Üzerindeki İsim"
                error={!!errors.cardHolder}
                helperText={errors.cardHolder?.message}
                {...register('cardHolder')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Kart Numarası"
                error={!!errors.cardNumber}
                helperText={errors.cardNumber?.message}
                {...register('cardNumber')}
                slotProps={{
                  input: {
                    endAdornment: binBank ? <Chip size="small" label={binBank} /> : undefined,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Ay (AA)"
                placeholder="09"
                error={!!errors.expirationMonth}
                helperText={errors.expirationMonth?.message}
                {...register('expirationMonth')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Yıl (YY)"
                placeholder="30"
                error={!!errors.expirationYear}
                helperText={errors.expirationYear?.message}
                {...register('expirationYear')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="CVV"
                error={!!errors.cvv}
                helperText={errors.cvv?.message}
                {...register('cvv')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              {installments.length > 0 ? (
                <Controller
                  control={control}
                  name="installmentSelection"
                  render={({ field }) => (
                    <TextField select fullWidth size="small" label="Taksit" {...field}>
                      {installments.map((option) => (
                        <MenuItem
                          key={option.installmentOptionId ?? option.installmentCount ?? 1}
                          value={option.installmentOptionId ?? String(option.installmentCount ?? 1)}
                        >
                          {installmentLabel(option)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Taksit"
                  type="number"
                  slotProps={{ htmlInput: { min: 1 } }}
                  {...register('installmentSelection')}
                />
              )}
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Controller
                control={control}
                name="threeDSecure"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={threeDPaymentAllowed}
                  />
                )}
              />
            }
            label="3D Secure"
          />
        </>
      )}

      {!useCurrentPayment && !creditCardPaymentAllowed && (
        <Alert severity="warning">Bu kullanıcı için kredi kartı ile ödeme yetkisi bulunmuyor.</Alert>
      )}

      <FormControlLabel
        control={
          <Controller
            control={control}
            name="acceptTos"
            render={({ field }) => (
              <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
            )}
          />
        }
        label="Kişisel Verilerin Korunması ve Gizlilik Politikası, Acente ve Bilet Kurallarını okudum, kabul ediyorum."
      />
      {errors.acceptTos && (
        <Typography variant="caption" color="error">
          {errors.acceptTos.message}
        </Typography>
      )}

      <Stack direction="row">
        <Button type="submit" variant="contained" disabled={loading}>
          {loading ? 'Ödeme işleniyor…' : submitLabel}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Kart bilgileri yalnızca bu isteği MakePayment uç noktasına iletmek için kullanılır.
      </Typography>
    </Box>
  );
}
